import React from "react"
import {
  EmailShareButton,
  FacebookMessengerShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  RedditShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share"
import { BottomSheet } from "react-spring-bottom-sheet"
import "react-spring-bottom-sheet/dist/style.css"

import FancyText from "@fuse/sharedComponents/typography/FancyText"

import { DeepLinkPath } from "@fuse/utils/DeviceDection"

import {
  TDeviceType,
  useDeviceTypeByWidth,
} from "@fuse/customHooks/useDeviceTypeByWidth"

import IconCopy from "@fuse/assets/images/eventSignUp/img-icon-copy.svg"
import IconFacebook from "@fuse/assets/images/socialMedia/img-facebook.png"
import IconFBMessenger from "@fuse/assets/images/socialMedia/img-fb-messenger.png"
import IconGmail from "@fuse/assets/images/socialMedia/img-gmail.png"
import IconLinkedin from "@fuse/assets/images/socialMedia/img-linkedin.png"
import IconReddit from "@fuse/assets/images/socialMedia/img-reddit.png"
import IconTelegram from "@fuse/assets/images/socialMedia/img-telegram.png"
import IconTwitter from "@fuse/assets/images/socialMedia/img-twitter.png"
import IconWhatsapp from "@fuse/assets/images/socialMedia/img-whatsapp.png"

interface ShareModalProps {
  show: boolean
  onClose: () => void
  shareData: { url: string; title: string; text: string; type?: DeepLinkPath }
}

const ShareModal: React.FC<ShareModalProps> = ({
  show,
  onClose,
  shareData,
}) => {
  const deviceType = useDeviceTypeByWidth()

  if (!show) {
    return null
  }

  const isMobileDevice = deviceType === TDeviceType.MOBILE
  const { url, title, text, type } = shareData

  const copyToClipboard = () => {
    navigator.clipboard.writeText(url)
    alert("Link copied to clipboard!")
    onClose()
  }

  const getTitle = () => {
    switch (type) {
      case DeepLinkPath.Events:
        return "Share this event"
      case DeepLinkPath.Ticket:
        return "Share this Ticket"
      case DeepLinkPath.ClaimTicket:
        return "Share this to claim ticket"
      case DeepLinkPath.Groups:
        return "Share this group"
      case DeepLinkPath.Activity:
        return "Share this activity"
      default:
        return "Share this"
    }
  }

  const handleEmailOnClick = () => {
    const subject = "Check out this event on Let's Hang!"
    const body = `Hi,\n\nI wanted to share this event with you: ${url}]`

    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  const renderContent = () => (
    <>
      <FancyText className="text-[18px] mb-6 text-left font-medium text-white">
        {getTitle()}
      </FancyText>
      <div className="grid grid-cols-4 gap-6 mb-8">
        <WhatsappShareButton
          url={url}
          title={title}
          separator=": "
          className="flex h-[72px] w-[72px] items-center justify-center rounded-xl bg-black/20 hover:bg-black/30 transition"
        >
          <img src={IconWhatsapp} alt="IconWhatsapp" className="h-8 w-8" />
        </WhatsappShareButton>
        <FacebookShareButton
          url={url}
          title={text}
          className="flex h-[72px] w-[72px] items-center justify-center rounded-xl bg-black/20 hover:bg-black/30 transition"
        >
          <img src={IconFacebook} alt="IconFacebook" className="h-8 w-8" />
        </FacebookShareButton>
        <EmailShareButton
          url={url}
          onClick={handleEmailOnClick}
          className="flex h-[72px] w-[72px] items-center justify-center rounded-xl bg-black/20 hover:bg-black/30 transition"
        >
          <img src={IconGmail} alt="IconGmail" className="h-8 w-8" />
        </EmailShareButton>
        <TwitterShareButton
          url={url}
          title={title}
          className="flex h-[72px] w-[72px] items-center justify-center rounded-xl bg-black/20 hover:bg-black/30 transition"
        >
          <img src={IconTwitter} alt="IconTwitter" className="h-8 w-8" />
        </TwitterShareButton>
        <LinkedinShareButton
          url={url}
          className="flex h-[72px] w-[72px] items-center justify-center rounded-xl bg-black/20 hover:bg-black/30 transition"
        >
          <img src={IconLinkedin} alt="IconLinkedin" className="h-8 w-8" />
        </LinkedinShareButton>
        <RedditShareButton
          url={url}
          title={title}
          className="flex h-[72px] w-[72px] items-center justify-center rounded-xl bg-black/20 hover:bg-black/30 transition"
        >
          <img src={IconReddit} alt="IconReddit" className="h-8 w-8" />
        </RedditShareButton>
        <FacebookMessengerShareButton
          url={url}
          appId="YOUR_APP_ID"
          className="flex h-[72px] w-[72px] items-center justify-center rounded-xl bg-black/20 hover:bg-black/30 transition"
        >
          <img
            src={IconFBMessenger}
            alt="IconFBMessenger"
            className="h-8 w-8"
          />
        </FacebookMessengerShareButton>
        <TelegramShareButton
          url={url}
          title={title}
          className="flex h-[72px] w-[72px] items-center justify-center rounded-xl bg-black/20 hover:bg-black/30 transition"
        >
          <img src={IconTelegram} alt="IconTelegram" className="h-8 w-8" />
        </TelegramShareButton>
      </div>
      <div className="h-[1px] w-full rounded-full bg-white/10 my-8"></div>
      <div className="rounded-xl p-4">
        <FancyText className="text-[18px] mb-2 text-left font-medium text-white">
          Share the link
        </FancyText>
        <div className="flex items-center justify-between">
          <span className="truncate font-normal text-[#A1A4B3] mr-4 text-sm max-w-[70%]">
            {url}
          </span>
          <button
            onClick={copyToClipboard}
            className="bg-transparent p-0 border-transparent focus:border-transparent focus:ring-0 focus:outline-none"
          >
            <img src={IconCopy} alt="IconCopy" className="h-7 w-7" />
          </button>
        </div>
      </div>
    </>
  )

  return isMobileDevice ? (
    <BottomSheet
      open={show}
      onDismiss={onClose}
      className="!bg-transparent"
      style={
        {
          "--rsbs-bg": "transparent",
          "--rsbs-handle-bg": "#999999",
        } as React.CSSProperties
      }
    >
      <div className="p-4  bg-white bg-opacity-20 backdrop-blur-md rounded-2xl">
        {renderContent()}
      </div>
    </BottomSheet>
  ) : (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="max-w-lg w-full bg-white bg-opacity-20 backdrop-blur-md mx-auto rounded-2xl shadow-lg p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {renderContent()}
      </div>
    </div>
  )
}

export default ShareModal
